import React, { useState, useEffect } from 'react';
import Text from '../../components/Text.js';
import Button from '../../components/Button.js';
import Modal from '../../components/Modals/Modal.js';
import colors from '../../theme/colors.js';
import Details from './Details.js';
import MemberChange from './MemberChange.js';
import DependentChange from './DependentChange.js';
import BeneficiaryChange from './BeneficiaryChange.js';
import TrusteeChange from './TrusteeChange.js';
import CoverageChange from './CoverageChange.js';
import Authorization from './Authorization.js';
import Attachments from './Attachments.js';
import validateForm from './validateForm.js';
import * as localizationMaps from '../../localization/maps.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import * as dates from '@simplybenefits/sb-dates';

/**
 * @param data Data from Change Request stored in S3
 * @param accessType Agent, Member, Administrator
 * @param approveChangeRequest Function for approving request
 * @param declineChangeRequest Function for declining request
 * @param saveChangeRequest Function for saving request
 * File System props
 * @param id
 * @param dispatch
 * @param type
 * @param listFiles
 * @param createFolder
 * @param uploadFile
 * @param downloadFile
 * @param removeFile
 */
function ChangeRequestForm({
  data,
  accessType,
  agentWriteAccess = false,
  approveChangeRequest,
  declineChangeRequest,
  saveChangeRequest,
  id,
  dispatch,
  loader,
  type,
  listFiles,
  createFolder,
  uploadFile,
  downloadFile,
  removeFile
}) {
  const accessData = {
    accessType,
    isAgent: accessType.toUpperCase() === 'AGENT' || false,
    agentWriteAccess: agentWriteAccess || false
  };
  const [errorState, setErrorState] = useState({});
  const [openApproveModal, setApproveModal] = useState(false);
  const [openDeclineModal, setDeclineModal] = useState(false);
  const [statusColor, setStatusColor] = useState(colors.darkBlue);
  const [isApproveDeclineVisible, setIsApproveDeclineVisible] = useState(false);
  const [signageSuccess, setSignageSuccess] = useState(false);
  const [changeState, setChangeState] = useState({
    changeRequestUUID: '',
    status: '',
    requesterName: '',
    requesterType: '',
    identifier: '',
    submittedDate: '',
    company: '',
    groupNum: '',
    division: '',
    cert: '',
    firstname: '',
    lastname: '',
    effectiveDate: '',
    reasonForChange: '',
    type: '',
    oldData: {},
    newData: {},
    internalNotes: [],
    externalNotes: [],
    signage: {
      member: {
        signature: '',
        datedOn: ''
      },
      admin: {
        signature: '',
        datedOn: ''
      }
    }
  });
  useEffect(() => {
    setChangeState({
      ...data,
      submittedDate: dates.formatIncomingDate(data.submittedDate),
      effectiveDate: dates.formatIncomingDate(data.effectiveDate),
      oldData: {
        ...data.oldData,
        // Only format if they exist
        ...(data.oldData.birthdate && {
          birthdate: dates.formatIncomingDate(data.oldData.birthdate)
        }),
        ...(data.oldData.cohabDate && {
          cohabDate: dates.formatIncomingDate(data.oldData.cohabDate)
        }),
        ...(data.oldData.dateOfMarriage && {
          dateOfMarriage: dates.formatIncomingDate(data.oldData.dateOfMarriage)
        }),
        ...(data.oldData.depCovReinstateStartDate && {
          depCovReinstateStartDate: dates.formatIncomingDate(data.oldData.depCovReinstateStartDate)
        }),
        ...(data.oldData.depCovReinstateEndDate && {
          depCovReinstateEndDate: dates.formatIncomingDate(data.oldData.depCovReinstateEndDate)
        }),
        ...(data.oldData.hireDate && {
          hireDate: dates.formatIncomingDate(data.oldData.hireDate)
        })
      },
      newData: {
        ...data.newData,
        // Only format if they exist
        ...(data.newData.birthdate && {
          birthdate: dates.formatIncomingDate(data.newData.birthdate)
        }),
        ...(data.newData.cohabDate && {
          cohabDate: dates.formatIncomingDate(data.newData.cohabDate)
        }),
        ...(data.newData.dateOfMarriage && {
          dateOfMarriage: dates.formatIncomingDate(data.newData.dateOfMarriage)
        }),
        ...(data.newData.depCovReinstateStartDate && {
          depCovReinstateStartDate: dates.formatIncomingDate(data.newData.depCovReinstateStartDate)
        }),
        ...(data.newData.depCovReinstateEndDate && {
          depCovReinstateEndDate: dates.formatIncomingDate(data.newData.depCovReinstateEndDate)
        }),
        ...(data.newData.hireDate && {
          hireDate: dates.formatIncomingDate(data.newData.hireDate)
        })
      },
      internalNotes: data.internalNotes || [],
      externalNotes: data.externalNotes || [],
      signage: {
        ...data.signage,
        member: {
          ...data.signage.member,
          signature: data.signage?.member?.signature || '',
          datedOn: data.signage?.member?.datedOn ? dates.formatIncomingDate(data.signage.member.datedOn) : ''
        },
        admin: {
          ...data.signage.admin,
          signature: data.signage?.admin?.signature || '',
          datedOn: data.signage?.admin?.datedOn ? dates.formatIncomingDate(data.signage.admin.datedOn) : ''
        }
      }
    });
  }, [data]);

  /**
   *
   * @param formData
   * @returns {{identifier, requesterType: (string|*), firstname, externalNotes: ([]|*[]|*|*[]), groupNum: (string|*), cert: *, internalNotes: ([]|string|*|*[]), submittedDate: *, type, reasonForChange: (string|*), lastname, division: (string|*), oldData, requesterName: (string|*), company: (string|*), signage: (*&{member: (*&{datedOn: *}), admin: (*&{datedOn: *})}), changeRequestUUID: (string|null|*), effectiveDate: *, newData, status}}
   * @private
   */
  function _formatOutGoing(formData) {
    return {
      ...formData,
      submittedDate: dates.formatOutgoingDate(formData.submittedDate),
      effectiveDate: dates.formatOutgoingDate(formData.effectiveDate),
      oldData: {
        ...formData.oldData,
        // Only format if they exist
        ...(formData.oldData.birthdate && {
          birthdate: dates.formatOutgoingDate(formData.oldData.birthdate)
        }),
        ...(formData.oldData.cohabDate && {
          cohabDate: dates.formatOutgoingDate(formData.oldData.cohabDate)
        }),
        ...(data.oldData.dateOfMarriage && {
          dateOfMarriage: dates.formatOutgoingDate(data.oldData.dateOfMarriage)
        }),
        ...(formData.oldData.depCovReinstateStartDate && {
          depCovReinstateStartDate: dates.formatOutgoingDate(formData.oldData.depCovReinstateStartDate)
        }),
        ...(formData.oldData.depCovReinstateEndDate && {
          depCovReinstateEndDate: dates.formatOutgoingDate(formData.oldData.depCovReinstateEndDate)
        }),
        ...(formData.oldData.hireDate && {
          hireDate: dates.formatOutgoingDate(formData.oldData.hireDate)
        })
      },
      newData: {
        ...formData.newData,
        // Only format if they exist
        ...(formData.newData.birthdate && {
          birthdate: dates.formatOutgoingDate(formData.newData.birthdate)
        }),
        ...(formData.newData.cohabDate && {
          cohabDate: dates.formatOutgoingDate(formData.newData.cohabDate)
        }),
        ...(data.newData.dateOfMarriage && {
          dateOfMarriage: dates.formatOutgoingDate(data.newData.dateOfMarriage)
        }),
        ...(formData.newData.depCovReinstateStartDate && {
          depCovReinstateStartDate: dates.formatOutgoingDate(formData.newData.depCovReinstateStartDate)
        }),
        ...(formData.newData.depCovReinstateEndDate && {
          depCovReinstateEndDate: dates.formatOutgoingDate(formData.newData.depCovReinstateEndDate)
        }),
        ...(formData.newData.maritlStat && {
          maritlStat: localizationMaps.toEnglish("maritalStatuses", formData.newData.maritlStat)
        }),
        ...(formData.newData.gender && {
          gender: localizationMaps.toEnglish("genders", formData.newData.gender)
        }),
        ...(formData.newData.hireDate && {
          hireDate: dates.formatOutgoingDate(formData.newData.hireDate)
        })
      },
      internalNotes: formData.internalNotes || [],
      externalNotes: formData.externalNotes || [],
      signage: {
        ...formData.signage,
        member: {
          ...formData.signage.member,
          datedOn: dates.formatOutgoingDate(formData.signage.member.datedOn)
        },
        admin: {
          ...formData.signage.admin,
          datedOn: dates.formatOutgoingDate(formData.signage.admin.datedOn)
        }
      }
    };
  }

  /**
   * Handles input updates
   * @param {*} key 
   * @param {*} value 
   * @param {*} section Used for specifying the sub sections of the object update
   * @param {*} arrayIndex Used for updating the shares within the Contingent/Primary arrays
   */
  function _handleChange(key, value, section, arrayIndex) {
    switch (section) {
      case 'newData':
        setChangeState(prevState => ({
          ...prevState,
          newData: {
            ...prevState.newData,
            [key]: value
          }
        }));
        break;
      case 'contingent':
      case 'primary':
        setChangeState(prevState => ({
          ...prevState,
          newData: {
            ...prevState.newData,
            [section]: prevState.newData[section].map((item, index) => index === arrayIndex ? {
              ...item,
              [key]: value
            } : item) // Updating shares in contingent or Primary Array
          }
        }));
        break;
      case 'signageMember':
        setChangeState(prevState => ({
          ...prevState,
          signage: {
            ...prevState.signage,
            member: {
              ...prevState.signage.member,
              [key]: value
            }
          }
        }));
        break;
      case 'signageAdmin':
        setChangeState(prevState => ({
          ...prevState,
          signage: {
            ...prevState.signage,
            admin: {
              ...prevState.signage.admin,
              [key]: value
            }
          }
        }));
        break;
      default:
        setChangeState(prevState => ({
          ...prevState,
          [key]: value
        }));
    }
  }

  /**
   * Adjust displayed status depending on user type
   * @returns status to display to user
   */
  function _getStatusToDisplay() {
    const normalizedAccessType = accessData.accessType.toUpperCase();
    if (normalizedAccessType === 'AGENT') {
      return localizationMaps.fromEnglish('changeRequestStatuses', changeState.status);
    }

    // Member and Admin Statuses
    if (changeState.status === 'Pending Agent Review') {
      return localizationMaps.fromEnglish('changeRequestStatuses', 'Pending');
    }
    if (changeState.status === 'Pending Admin Review') {
      return normalizedAccessType === 'MEMBER' ? localizationMaps.fromEnglish('changeRequestStatuses', 'Pending') : localizationMaps.fromEnglish('changeRequestStatuses', 'Needs Review');
    }
    if (changeState.status === 'Pending Member Review') {
      return normalizedAccessType === 'MEMBER' ? localizationMaps.fromEnglish('changeRequestStatuses', 'Needs Review') : localizationMaps.fromEnglish('changeRequestStatuses', 'Pending');
    }
    if (['Incomplete', 'Suspended'].includes(changeState.status)) {
      return localizationMaps.fromEnglish('changeRequestStatuses', 'Pending');
    }
    if (changeState.status === 'Reversed') {
      return localizationMaps.fromEnglish('changeRequestStatuses', 'Declined');
    }
    return localizationMaps.fromEnglish('changeRequestStatuses', changeState.status);
  }

  /**
   * Selects the status color
   * @param {*} status 
   * @returns 
   */
  function _getStatusColor(status) {
    switch (status) {
      case 'Approved':
        return colors.lightGreen;
      case 'Declined':
        return colors.red;
      default:
        return colors.darkBlue;
    }
  }

  /**
   * Determine if approve/decline buttons should show
   * @param {*} status 
   * @returns 
   */
  function _shouldApproveDeclineBeVisible(status) {
    switch (accessData.accessType.toUpperCase()) {
      case 'AGENT':
        return accessData.agentWriteAccess && status === 'Pending Agent Review';
      case 'ADMINISTRATOR':
        return status === 'Pending Admin Review';
      case 'MEMBER':
        return status === 'Pending Member Review';
      default:
        return false;
    }
  }

  /**
   * Validate any editable data
   * @param validationType
   * @returns 
   */
  async function _validateData(validationType) {
    const errors = await validateForm(changeState);
    if (!errors) {
      setErrorState({});
      const outgoing = _formatOutGoing(changeState);
      switch (validationType) {
        case 'Approve':
          setApproveModal(true);
          break;
        case 'Decline':
          setDeclineModal(true);
          break;
        case 'Save':
          saveChangeRequest(outgoing);
          break;
        default:
          return false;
      }
    }
    setErrorState({
      ...errors
    });
    return true;
  }

  /**
   * Handler for approving change requests
   */
  async function _approveChangeRequest() {
    const outgoing = _formatOutGoing(changeState);
    const success = await approveChangeRequest(outgoing);
    if (success) {
      setSignageSuccess(true);
      setApproveModal(false);
    }
  }

  /**
   * Handler for declining change requests
   */
  async function _declineChangeRequest() {
    const outgoing = _formatOutGoing(changeState);
    const success = await declineChangeRequest(outgoing);
    if (success) {
      setSignageSuccess(true);
      setDeclineModal(false);
    }
  }

  /**
   * Adjust status styling and button display when status changes.
   */
  useEffect(() => {
    setStatusColor(_getStatusColor(changeState.status));
    setIsApproveDeclineVisible(_shouldApproveDeclineBeVisible(changeState.status));
  }, [changeState.status]);
  const styles = {
    content: {
      backgroundColor: colors.surface,
      boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
      display: 'flex',
      flexDirection: 'column',
      margin: 'auto auto auto auto',
      padding: '40px 60px',
      position: 'relative'
    },
    title: {
      marginBottom: '25px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    buttonWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '10px'
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    open: openApproveModal,
    title: i18n.t(tokens.labels.changeRequests.approveChangeRequest),
    text: i18n.t(tokens.labels.changeRequests.approveText),
    onClose: () => setApproveModal(false),
    primaryButton: {
      label: `${i18n.t(tokens.labels.changeRequests.approveChangeRequest)}`,
      onClick: () => _approveChangeRequest()
    },
    loader: loader
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true
  }, i18n.t(tokens.labels.changeRequests.approveText))), /*#__PURE__*/React.createElement(Modal, {
    open: openDeclineModal,
    title: i18n.t(tokens.labels.changeRequests.declineChangeRequest),
    text: i18n.t(tokens.labels.changeRequests.declineText),
    onClose: () => setDeclineModal(false),
    primaryButton: {
      label: `${i18n.t(tokens.labels.changeRequests.declineChangeRequest)}`,
      onClick: () => _declineChangeRequest()
    },
    loader: loader
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true
  }, i18n.t(tokens.labels.changeRequests.declineText))), /*#__PURE__*/React.createElement("div", {
    style: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.header
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    extraLarge: true,
    customStyle: styles.title
  }, i18n.t(tokens.titles.groupChangeForm)), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    mediumLarge: true,
    customStyle: {
      ...styles.title,
      color: `${statusColor}`
    }
  }, _getStatusToDisplay())), /*#__PURE__*/React.createElement(Details, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), (changeState.type === 'Edit Member Details' || changeState.type === 'Edit Employment Details') && /*#__PURE__*/React.createElement(MemberChange, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), ['Add a Dependent', 'Remove a Dependent', 'Edit Dependent Details'].includes(changeState.type) && /*#__PURE__*/React.createElement(DependentChange, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), ['Add Irrevocable Beneficiary', 'Edit Irrevocable Beneficiary', 'Remove Irrevocable Beneficiary', 'Add Revocable Beneficiary', 'Edit Revocable Beneficiary', 'Remove Revocable Beneficiary'].includes(changeState.type) && /*#__PURE__*/React.createElement(BeneficiaryChange, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), ['Add Trustee', 'Edit Trustee', 'Remove Trustee'].includes(changeState.type) && /*#__PURE__*/React.createElement(TrusteeChange, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), ['Waive Benefits', 'Reinstate Benefits'].includes(changeState.type) && /*#__PURE__*/React.createElement(CoverageChange, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange
  }), /*#__PURE__*/React.createElement(Attachments, {
    data: changeState,
    handleChange: _handleChange,
    accessData: accessData

    // Files
    ,
    id: id,
    dispatch: dispatch,
    type: type,
    listFiles: listFiles,
    createFolder: createFolder,
    uploadFile: uploadFile,
    downloadFile: downloadFile,
    removeFile: removeFile
  }), /*#__PURE__*/React.createElement(Authorization, {
    data: changeState,
    errors: errorState,
    accessData: accessData,
    handleChange: _handleChange,
    signageSuccess: signageSuccess
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.buttonWrapper
  }, accessData.isAgent && accessData.agentWriteAccess && /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => _validateData('Save')
  }, i18n.t(tokens.buttons.save)), isApproveDeclineVisible && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    customStyle: {
      marginLeft: '10px'
    },
    onClick: () => _validateData('Decline'),
    disabled: signageSuccess
  }, i18n.t(tokens.buttons.decline)), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    customStyle: {
      marginLeft: '10px'
    },
    onClick: () => _validateData('Approve'),
    disabled: signageSuccess
  }, i18n.t(tokens.buttons.approve))))));
}
export default ChangeRequestForm;